import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';
import ToastHost, { useToast } from '../shared/Toast';

const inputStyle = { background: colors.cardBg, color: colors.textPrimary, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, padding: '0.5rem 0.75rem', width: '100%', fontFamily, boxSizing: 'border-box' };
const labelStyle = { display: 'block', marginBottom: '0.3rem', color: colors.textMuted, fontSize: '0.85rem' };

const DEFAULT_NEW_USER = {
  username: '', password: '', name: '', role: 'agent', agentId: '',
  weeklyLimit: 0, minBet: 0.01, maxBet: 0,
  parlayMinBet: 0.01, parlayMaxBet: 0, parlayMaxPayout: 0,
  liveMinBet: 0.01, liveMaxBet: 100, casinoMinBet: 0.01, casinoMaxBet: 100,
  casinoWeeklyMaxWin: 0, casinoWeeklyMaxLoss: 0,
  agentCut: 30, freePlayLossPercentage: 30, gameLimit: 0,
  sportsbookAccess: true, casinoAccess: true
};

// Per-role starting values for the limit fields that differ between roles.
// Applied when the Role dropdown changes so a customer never inherits an
// agent's limits (and vice-versa). Agent values are further overridden from
// the book's Default Agent Template on mount.
const ROLE_LIMIT_DEFAULTS = {
  agent: { weeklyLimit: 0, maxBet: 0, parlayMaxBet: 0 },
  customer: { weeklyLimit: 100, maxBet: 100, parlayMaxBet: 100 },
};

export default function CreateAccountSection({ user }) {
  const [newUser, setNewUser] = useState(DEFAULT_NEW_USER);
  const [saving, setSaving] = useState(false);
  const [agents, setAgents] = useState([]);
  const { toast, showToast } = useToast();

  useEffect(() => {
    api.getAccounts().then(data => setAgents((data.users || []).filter(u => u.role === 'agent'))).catch(() => setAgents([]));
    // Pre-fill the agent-creation fields from the book's Default Agent
    // Template instead of the hardcoded literals above, so a new agent
    // starts from whatever baseline the book has configured. Only affects
    // the form's starting values — nothing here overrides what the book
    // actually types before submitting.
    api.getAgentDefaultTemplate().then(t => {
      setNewUser(prev => prev.role !== 'agent' ? prev : {
        ...prev,
        weeklyLimit: t.weekly_limit,
        minBet: t.min_bet,
        maxBet: t.max_bet,
        parlayMinBet: t.parlay_min_bet,
        parlayMaxBet: t.parlay_max_bet,
        agentCut: t.agent_cut,
      });
    }).catch(() => {});
  }, []);

  const handleCreateAccount = async () => {
    setSaving(true);
    try {
      const result = await api.createAccount({
        username: newUser.username, password: newUser.password, name: newUser.name,
        role: newUser.role, weekly_limit: newUser.weeklyLimit, min_bet: newUser.minBet,
        max_bet: newUser.maxBet, parlay_min_bet: newUser.parlayMinBet, parlay_max_bet: newUser.parlayMaxBet,
        live_min_bet: newUser.liveMinBet, live_max_bet: newUser.liveMaxBet,
        casino_min_bet: newUser.role === 'customer' ? newUser.casinoMinBet : undefined,
        casino_max_bet: newUser.role === 'customer' ? newUser.casinoMaxBet : undefined,
        casino_weekly_max_win: newUser.role === 'customer' ? newUser.casinoWeeklyMaxWin : undefined,
        casino_weekly_max_loss: newUser.role === 'customer' ? newUser.casinoWeeklyMaxLoss : undefined,
        parlay_max_payout: newUser.role === 'customer' ? newUser.parlayMaxPayout : undefined,
        agent_id: newUser.role === 'customer' && newUser.agentId ? Number(newUser.agentId) : undefined,
        agent_cut: newUser.role === 'agent' ? newUser.agentCut : undefined,
        free_play_loss_percentage: newUser.role === 'customer' ? newUser.freePlayLossPercentage : undefined,
        game_limit: newUser.role === 'customer' ? newUser.gameLimit : undefined,
        sportsbook_access: newUser.role === 'customer' ? newUser.sportsbookAccess : undefined,
        casino_access: newUser.role === 'customer' ? newUser.casinoAccess : undefined
      });
      showToast(`Success! ${result.message || 'Account created'}`);
      setNewUser(DEFAULT_NEW_USER);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create account', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ color: colors.textPrimary, fontFamily }}>
      <ToastHost toast={toast} />
      <h2 style={{ color: colors.textPrimary, marginBottom: '1rem' }}>Create Account</h2>
      <div style={{ background: colors.cardBg, boxShadow: colors.shadowCard, borderRadius: 8, padding: '1.25rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={labelStyle}>Role</label>
            <select style={{ ...inputStyle, backgroundColor: '#1a0822' }} value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value, ...(ROLE_LIMIT_DEFAULTS[e.target.value] || {}) })}>
              <option style={{ backgroundColor: '#1a0822', color: colors.textPrimary }} value="agent">Agent</option>
              <option style={{ backgroundColor: '#1a0822', color: colors.textPrimary }} value="customer">Customer</option>
            </select>
          </div>
          {newUser.role === 'customer' && (
            <div>
              <label style={labelStyle}>Agent</label>
              <select style={{ ...inputStyle, backgroundColor: '#1a0822' }} value={newUser.agentId} onChange={e => setNewUser({ ...newUser, agentId: e.target.value })}>
                <option style={{ backgroundColor: '#1a0822', color: colors.textPrimary }} value="">No agent (direct customer)</option>
                {agents.map(a => (
                  <option key={a.id} style={{ backgroundColor: '#1a0822', color: colors.textPrimary }} value={a.id}>{a.name} (@{a.username})</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={labelStyle}>Username</label>
            <input style={inputStyle} value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Password</label>
            <input type="password" style={inputStyle} value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Full Name</label>
            <input style={inputStyle} value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Weekly Limit</label>
            <input type="number" style={inputStyle} value={newUser.weeklyLimit} onChange={e => setNewUser({ ...newUser, weeklyLimit: Number(e.target.value) })} />
          </div>
          <div>
            <label style={labelStyle}>Min Bet</label>
            <input type="number" step="0.01" min="0.01" style={inputStyle} value={newUser.minBet} onChange={e => setNewUser({ ...newUser, minBet: Number(e.target.value) })} />
          </div>
          <div>
            <label style={labelStyle}>Max Bet</label>
            <input type="number" step="0.01" min="0" style={inputStyle} value={newUser.maxBet} onChange={e => setNewUser({ ...newUser, maxBet: Number(e.target.value) })} />
          </div>
          <div>
            <label style={labelStyle}>Parlay Min Bet</label>
            <input type="number" step="0.01" min="0.01" style={inputStyle} value={newUser.parlayMinBet} onChange={e => setNewUser({ ...newUser, parlayMinBet: Number(e.target.value) })} />
          </div>
          <div>
            <label style={labelStyle}>Parlay Max Bet</label>
            <input type="number" step="0.01" min="0" style={inputStyle} value={newUser.parlayMaxBet} onChange={e => setNewUser({ ...newUser, parlayMaxBet: Number(e.target.value) })} />
          </div>
          <div>
            <label style={labelStyle}>Live Min Bet</label>
            <input type="number" step="0.01" min="0.01" style={inputStyle} value={newUser.liveMinBet} onChange={e => setNewUser({ ...newUser, liveMinBet: Number(e.target.value) })} />
          </div>
          <div>
            <label style={labelStyle}>Live Max Bet</label>
            <input type="number" step="0.01" min="0" style={inputStyle} value={newUser.liveMaxBet} onChange={e => setNewUser({ ...newUser, liveMaxBet: Number(e.target.value) })} />
          </div>
          {newUser.role === 'customer' && (
            <>
              <div>
                <label style={labelStyle}>Parlay Max Payout</label>
                <input type="number" step="0.01" min="0" style={inputStyle} value={newUser.parlayMaxPayout} onChange={e => setNewUser({ ...newUser, parlayMaxPayout: Number(e.target.value) })} />
              </div>
              <div>
                <label style={labelStyle}>Contest Limit</label>
                <input type="number" step="0.01" min="0" style={inputStyle} value={newUser.gameLimit} onChange={e => setNewUser({ ...newUser, gameLimit: Number(e.target.value) })} />
              </div>
              <div>
                <label style={labelStyle}>Casino Min Bet</label>
                <input type="number" step="0.01" min="0.01" style={inputStyle} value={newUser.casinoMinBet} onChange={e => setNewUser({ ...newUser, casinoMinBet: Number(e.target.value) })} />
              </div>
              <div>
                <label style={labelStyle}>Casino Max Bet</label>
                <input type="number" step="0.01" min="0" style={inputStyle} value={newUser.casinoMaxBet} onChange={e => setNewUser({ ...newUser, casinoMaxBet: Number(e.target.value) })} />
              </div>
              <div>
                <label style={labelStyle}>Casino Weekly Max Win (0 = unlimited)</label>
                <input type="number" step="0.01" min="0" style={inputStyle} value={newUser.casinoWeeklyMaxWin} onChange={e => setNewUser({ ...newUser, casinoWeeklyMaxWin: Number(e.target.value) })} />
              </div>
              <div>
                <label style={labelStyle}>Casino Weekly Max Loss (0 = unlimited)</label>
                <input type="number" step="0.01" min="0" style={inputStyle} value={newUser.casinoWeeklyMaxLoss} onChange={e => setNewUser({ ...newUser, casinoWeeklyMaxLoss: Number(e.target.value) })} />
              </div>
              <div>
                <label style={labelStyle}>Free Play Loss Rebate (%)</label>
                <input type="number" step="1" min="0" max="100" style={inputStyle} value={newUser.freePlayLossPercentage} onChange={e => setNewUser({ ...newUser, freePlayLossPercentage: Number(e.target.value) })} />
              </div>
              <div>
                <label style={labelStyle}>Product Access</label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', paddingTop: '0.4rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: colors.textPrimary, fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={newUser.sportsbookAccess} onChange={e => setNewUser({ ...newUser, sportsbookAccess: e.target.checked })} />
                    Sportsbook
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: colors.textPrimary, fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={newUser.casinoAccess} onChange={e => setNewUser({ ...newUser, casinoAccess: e.target.checked })} />
                    Casino
                  </label>
                </div>
              </div>
            </>
          )}
          {newUser.role === 'agent' && (
            <div>
              <label style={labelStyle}>Agent Cut (%)</label>
              <input type="number" min="0" max="100" step="0.1" style={inputStyle} value={newUser.agentCut} onChange={e => setNewUser({ ...newUser, agentCut: Number(e.target.value) })} />
            </div>
          )}
        </div>
        <button
          onClick={handleCreateAccount}
          disabled={saving}
          style={{ marginTop: '1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.6rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontFamily }}
        >
          {saving ? 'Creating...' : 'Create Account'}
        </button>
      </div>
    </div>
  );
}
